import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRuntimeConfig } from '../contexts/RuntimeConfigContext';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../i18n';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Card,
  CardContent,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Inventory2 as Inventory2Icon,
  ReceiptLong as ReceiptLongIcon,
  Payments as PaymentsIcon,
  DesignServices as DesignServicesIcon,
  EventAvailable as EventAvailableIcon,
  Campaign as CampaignIcon,
  Logout as LogoutIcon,
  Storefront as StorefrontIcon,
  Visibility as VisibilityIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  CheckCircle as CheckCircleIcon,
  PeopleAlt as PeopleAltIcon,
  TrendingUp as TrendingUpIcon,
  StarBorder as StarBorderIcon,
  Verified as VerifiedIcon,
  AssignmentReturn as ReturnsIcon,
} from '@mui/icons-material';
import { providerApi, productApi } from '../services/api';
import { PROVIDER_SERVICE_CURRENCY_CODE } from '../utils/currencyUtils';
import logoImage from '../logo.png';

// Import sub-components
import ProviderProfile from './dashboard/ProviderProfile';
import CommissionCard from './dashboard/CommissionCard';
import PayoutsCard from './dashboard/PayoutsCard';
import ProGate from './plan/ProGate';
import ProTeaser from './plan/ProTeaser';
import ProBadge from './plan/ProBadge';
import UpgradePage from './dashboard/UpgradePage';
import AnalyticsPage from './dashboard/AnalyticsPage';
import ReviewsPage from './dashboard/ReviewsPage';
import { usePlan } from '../plan/usePlan';
import { FEATURES } from '../plan/plan';
import InfluencerStats from './dashboard/InfluencerStats';
import ServiceManagement from './dashboard/ServiceManagement';
import AvailabilitySettings from './dashboard/AvailabilitySettings';
import StoreManagement from './dashboard/StoreManagement';
import OrdersManagement from './dashboard/OrdersManagement';
import StoreReturns from './dashboard/StoreReturns';
import PaymentSetup from './dashboard/PaymentSetup';
import MyAudience from './dashboard/MyAudience';
import PromotionsPage from './dashboard/PromotionsPage';
import ConfirmationBanner from './dashboard/ConfirmationBanner';
import PayoutOnboardingBanner from './dashboard/PayoutOnboardingBanner';

const DRAWER_WIDTH = 256;

// ── Sidebar color tokens (mirrors admin panel) ──────────────────────────────
const S = {
  bg: '#18123a',
  text: 'rgba(255,255,255,0.62)',
  activeText: '#fff',
  activeBg: 'rgba(139,92,246,0.18)',
  activeBorder: '#8B5CF6',
  hover: 'rgba(255,255,255,0.05)',
  divider: 'rgba(255,255,255,0.08)',
  sectionLabel: 'rgba(255,255,255,0.28)',
};

// ── Stat color tokens ───────────────────────────────────────────────────────
const C = {
  primary: '#6D28D9',
  primaryLight: '#8B5CF6',
  blue: '#3B82F6',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  slate: '#64748B',
};

const fmt = (n) => (n == null ? '—' : (typeof n === 'number' ? Number(n).toLocaleString() : n));

// ── Reusable card shell ─────────────────────────────────────────────────────
function SectionCard({ title, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
  );
}

// ── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ label, value, caption, Icon, color, loading }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {label}
        </Typography>
        <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 36, height: 36, borderRadius: 1.5 }}>
          <Icon sx={{ fontSize: 18 }} />
        </Avatar>
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" sx={{ fontSize: '2rem' }} />
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}>
          {fmt(value)}
        </Typography>
      )}
      {caption != null && !loading && (
        <Typography variant="caption" color="text.disabled">
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

// STORE-only Returns queue. Tab numbers 0-10 are taken (9 upgrade, 10 promotions).
const TAB_RETURNS = 11;

// ── Navigation items derived from provider type ─────────────────────────────
const getNavItems = (providerType) => {
  const items = [
    { labelKey: 'nav.overview', Icon: DashboardIcon, value: 0 },
    { labelKey: 'nav.profile', Icon: PersonIcon, value: 1 },
  ];

  if (providerType === 'INFLUENCER') {
    items.push({ labelKey: 'nav.referrals', Icon: CampaignIcon, value: 2 });
    items.push({ labelKey: 'nav.audience', Icon: PeopleAltIcon, value: 3 });
  }

  if (providerType === 'SERVICE') {
    // Free tier keeps leads & discussion: Orders lives in the manage section (design §3).
    items.push({ labelKey: 'nav.services', Icon: DesignServicesIcon, value: 2 });
    items.push({ labelKey: 'nav.audience', Icon: PeopleAltIcon, value: 6 });
    items.push({ labelKey: 'nav.orders', Icon: ReceiptLongIcon, value: 4 });
    items.push({ labelKey: 'nav.availability', Icon: EventAvailableIcon, value: 3, section: 'pro', feature: FEATURES.AVAILABILITY });
    items.push({ labelKey: 'nav.payments', Icon: PaymentsIcon, value: 5, section: 'pro', feature: FEATURES.PAYMENTS });
    items.push({ labelKey: 'nav.analytics', Icon: TrendingUpIcon, value: 7, section: 'pro', feature: FEATURES.ANALYTICS });
    items.push({ labelKey: 'nav.reviews', Icon: StarBorderIcon, value: 8, section: 'pro', feature: FEATURES.REVIEWS });
    // Promotions — available to all providers, Free and Pro
    items.push({ labelKey: 'nav.promotions', Icon: CampaignIcon, value: 10 });
  }

  if (providerType === 'STORE') {
    items.push({ labelKey: 'nav.products', Icon: Inventory2Icon, value: 2 });
    items.push({ labelKey: 'nav.orders', Icon: ReceiptLongIcon, value: 3 });
    items.push({ labelKey: 'nav.returns', Icon: ReturnsIcon, value: TAB_RETURNS, badgeKey: 'returns' });
    items.push({ labelKey: 'nav.payments', Icon: PaymentsIcon, value: 4 });
    items.push({ labelKey: 'nav.audience', Icon: PeopleAltIcon, value: 5 });
  }

  return items;
};

// ── Sidebar content ─────────────────────────────────────────────────────────
function SidebarContent({ navItems, currentTab, onSelect, currentUser, providerType, onLogout, isPro }) {
  const { t, i18n } = useTranslation();
  const initials = currentUser?.email?.charAt(0).toUpperCase() ?? 'P';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: S.bg, overflow: 'hidden' }}>
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 64,
          px: 2.5,
          gap: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${S.divider}`,
        }}
      >
        <Avatar
          src={logoImage}
          alt="Anipaw"
          sx={{ width: 32, height: 32, flexShrink: 0 }}
          imgProps={{ draggable: false }}
        />
        <Box sx={{ overflow: 'hidden' }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.25, letterSpacing: '-0.01em' }}>
            Anipaw
          </Typography>
          <Typography sx={{ color: S.sectionLabel, fontSize: '0.68rem', lineHeight: 1 }}>
            {t('nav.dashboardSubtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Nav */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: S.divider, borderRadius: 4 },
        }}
      >
        <Typography
          sx={{
            px: 3,
            pb: 0.5,
            display: 'block',
            color: S.sectionLabel,
            fontWeight: 700,
            fontSize: '0.6rem',
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
          }}
        >
          {t('nav.sectionManage')}
        </Typography>
        <List disablePadding dense>
          {navItems.filter((i) => i.section !== 'pro').map((item) => {
            const isActive = currentTab === item.value;
            const { Icon } = item;
            return (
              <ListItem key={item.value} disablePadding>
                <ListItemButton
                  onClick={() => onSelect(item.value)}
                  sx={{
                    mx: 1,
                    my: 0.15,
                    px: 1.5,
                    py: 0.85,
                    borderRadius: 1.5,
                    borderLeft: isActive ? `3px solid ${S.activeBorder}` : '3px solid transparent',
                    bgcolor: isActive ? S.activeBg : 'transparent',
                    color: isActive ? S.activeText : S.text,
                    minHeight: 40,
                    transition: 'background-color 0.15s, color 0.15s',
                    '&:hover': {
                      bgcolor: isActive ? S.activeBg : S.hover,
                      color: S.activeText,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'inherit', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 400,
                      letterSpacing: isActive ? 0 : '-0.01em',
                    }}
                  />
                  {item.badge > 0 && (
                    <Chip
                      size="small"
                      color="error"
                      label={item.badge}
                      data-testid={`nav-badge-${item.badgeKey}`}
                      aria-label={t('nav.openCountLabel', { count: item.badge })}
                      sx={{ height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        {navItems.some((i) => i.section === 'pro') && (
          <>
            <Typography
              sx={{
                px: 3,
                pt: 1.5,
                pb: 0.5,
                display: 'block',
                color: S.sectionLabel,
                fontWeight: 700,
                fontSize: '0.6rem',
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
              }}
            >
              {t('nav.sectionPro')}
            </Typography>
            <List disablePadding dense>
              {navItems.filter((i) => i.section === 'pro').map((item) => {
                const isActive = currentTab === item.value;
                const { Icon } = item;
                return (
                  <ListItem key={item.value} disablePadding>
                    <ListItemButton
                      onClick={() => onSelect(item.value)}
                      sx={{
                        mx: 1,
                        my: 0.15,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: 1.5,
                        borderLeft: isActive ? `3px solid ${S.activeBorder}` : '3px solid transparent',
                        bgcolor: isActive ? S.activeBg : 'transparent',
                        color: isActive ? S.activeText : S.text,
                        minHeight: 40,
                        transition: 'background-color 0.15s, color 0.15s',
                        '&:hover': {
                          bgcolor: isActive ? S.activeBg : S.hover,
                          color: S.activeText,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: 'inherit', justifyContent: 'center' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.labelKey)}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive ? 600 : 400,
                          letterSpacing: isActive ? 0 : '-0.01em',
                        }}
                      />
                      {!isPro && <ProBadge />}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Box>

      {/* User footer */}
      <Box
        sx={{
          borderTop: `1px solid ${S.divider}`,
          px: 1.5,
          py: 1.25,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 30, height: 30,
              bgcolor: alpha('#8B5CF6', 0.5),
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#fff',
                fontSize: '0.78rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {currentUser?.email ?? ''}
            </Typography>
            <Typography sx={{ color: S.sectionLabel, fontSize: '0.65rem' }}>
              {t('nav.providerRole', { type: t('nav.type' + providerType) })}
            </Typography>
          </Box>
        </Box>
        <Typography
          role="button"
          onClick={() => setLanguage(i18n.language === 'he' ? 'en' : 'he')}
          sx={{ color: S.text, fontSize: '0.7rem', cursor: 'pointer', flexShrink: 0, '&:hover': { color: '#fff' } }}
        >
          {t('common.language')}
        </Typography>
        <Tooltip title={t('common.signOut')}>
          <IconButton
            size="small"
            onClick={onLogout}
            sx={{ color: S.text, flexShrink: 0, '&:hover': { color: '#fff', bgcolor: S.hover } }}
          >
            <LogoutIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const Dashboard = ({ providerData }) => {
  const { t } = useTranslation();
  const { paymentsEnabled, servicePaymentsEnabled, storePaymentsEnabled } = useRuntimeConfig();
  // Payout surfaces follow the switch for THIS provider's type (spec 2026-10-25). Influencers
  // have no type switch and keep the env-level flag.
  const typePaymentsEnabled = providerData.providerType === 'STORE'
    ? storePaymentsEnabled
    : providerData.providerType === 'SERVICE'
      ? servicePaymentsEnabled
      : paymentsEnabled;
  // "Online payments are coming soon" (spec §5): dismissible per browser until payments go live.
  const [comingSoonDismissed, setComingSoonDismissed] = useState(() => {
    try { return localStorage.getItem('comingSoonDismissed') === '1'; } catch (e) { return false; }
  });
  const dismissComingSoon = () => {
    setComingSoonDismissed(true);
    try { localStorage.setItem('comingSoonDismissed', '1'); } catch (e) { /* private mode */ }
  };
  const { signOut, currentUser, refreshProviderData } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const TAB_UPGRADE = 9;
  const TAB_PROMOTIONS = 10;
  // Orders is tab 4 for a SERVICE provider and 3 for a STORE one, which is EXACTLY why the
  // notification deep link is named rather than numeric (order-creation design §G).
  const TAB_ORDERS = providerData.providerType === 'STORE' ? 3 : 4;
  // Payments is tab 5 for SERVICE and 4 for STORE.
  const TAB_PAYMENTS = providerData.providerType === 'STORE' ? 4 : 5;
  // Named tab map — used for deep-linking and URL sync. SERVICE-only tabs (availability,
  // analytics, reviews) are omitted for STORE/INFLUENCER so ?tab=availability on a STORE
  // account doesn't accidentally resolve to tab 3 (which is Orders for STORE).
  const TAB_NAMES = {
    overview: 0,
    profile: 1,
    upgrade: TAB_UPGRADE,
    orders: TAB_ORDERS,
    promotions: TAB_PROMOTIONS,
    payments: TAB_PAYMENTS,
    ...(providerData.providerType === 'SERVICE' ? {
      services: 2,
      availability: 3,
      audience: 6,
      analytics: 7,
      reviews: 8,
    } : providerData.providerType === 'STORE' ? {
      products: 2,
      audience: 5,
      returns: TAB_RETURNS,
    } : providerData.providerType === 'INFLUENCER' ? {
      referrals: 2,
      audience: 3,
    } : {}),
  };
  const [currentTab, setCurrentTab] = useState(() => {
    const t = searchParams.get('tab');
    if (t && TAB_NAMES[t] !== undefined) return TAB_NAMES[t];
    const n = Number(t);
    return t !== null && t !== '' && Number.isInteger(n) ? n : 0;
  });
  const [visible, setVisible] = useState(providerData.visible || false);
  const plan = usePlan(providerData);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');
  const [visibilitySuccess, setVisibilitySuccess] = useState('');
  const currencyCode = PROVIDER_SERVICE_CURRENCY_CODE;

  // STORE nav badge: return requests waiting for this store's answer. Best-effort — a failed
  // count simply shows no badge.
  const [openReturns, setOpenReturns] = useState(0);
  const refreshOpenReturns = useCallback(() => {
    if (providerData.providerType !== 'STORE') return Promise.resolve();
    return Promise.resolve()
      .then(() => providerApi.getOpenStoreReturnsCount())
      .then((res) => setOpenReturns(Number(res?.count) || 0))
      .catch(() => {});
  }, [providerData.providerType]);
  useEffect(() => { refreshOpenReturns(); }, [refreshOpenReturns]);

  // Overview stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Load overview stats (best-effort; falls back gracefully)
  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      setStatsLoading(true);
      const next = {};
      try {
        if (providerData.providerType === 'STORE') {
          const [productStats, orders] = await Promise.allSettled([
            productApi.getProductStats(providerData.id),
            providerApi.getProviderOrders(providerData.id, 0, 1),
          ]);
          if (productStats.status === 'fulfilled') {
            next.products = productStats.value?.total_products ?? null;
            next.activeProducts = productStats.value?.active_products ?? null;
          }
          if (orders.status === 'fulfilled') {
            next.orders = orders.value?.totalElements ?? null;
          }
        } else if (providerData.providerType === 'SERVICE') {
          const orders = await providerApi.getProviderOrders(providerData.id, 0, 1).catch(() => null);
          next.orders = orders?.totalElements ?? null;
          next.services = providerData.services?.length ?? null;
        } else if (providerData.providerType === 'INFLUENCER') {
          const referrals = await providerApi.getReferralCount().catch(() => null);
          next.referrals = referrals?.count ?? null;
        }
      } catch (error) {
        console.error('Failed to load overview stats:', error);
      } finally {
        if (!cancelled) {
          setStats(next);
          setStatsLoading(false);
        }
      }
    };
    loadStats();
    return () => { cancelled = true; };
  }, [providerData.id, providerData.providerType, providerData.services]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const handleSelectTab = (value) => {
    setCurrentTab(value);
    setMobileOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const entry = Object.entries(TAB_NAMES).find(([, v]) => v === value);
      if (entry) next.set('tab', entry[0]);
      else { next.delete('tab'); next.delete('orderId'); }
      return next;
    }, { replace: true });
  };

  const goToUpgrade = () => handleSelectTab(TAB_UPGRADE);

  // Proactively sync PayPal payout onboarding status so the banner disappears without
  // requiring a visit to the Payments tab. The backend re-checks payments_receivable live
  // and persists COMPLETED to the DB; refreshProviderData() then picks it up.
  useEffect(() => {
    if (
      providerData.isProPlan &&
      providerData.paypalMerchantId &&
      providerData.paypalOnboardingStatus !== 'COMPLETED'
    ) {
      providerApi.getPaypalOnboardingStatus()
        .then((data) => { if (data?.status === 'COMPLETED') refreshProviderData(); })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server gate backstop: any API call rejected with 403 PRO_REQUIRED routes
  // the provider to the upgrade view (belt-and-suspenders with client gating).
  useEffect(() => {
    const onProRequired = () => setCurrentTab(TAB_UPGRADE);
    window.addEventListener('anipaw:pro-required', onProRequired);
    return () => window.removeEventListener('anipaw:pro-required', onProRequired);
  }, []);

  // A profile may only go visible once it has at least one service with a price — otherwise a
  // customer who finds it (and taps "Let's Talk") hits "provider offers no service". Seeded
  // profiles can arrive visible-but-serviceless after a claim, so this is enforced here too.
  const hasPricedService = (providerData.services || []).some((s) => Number(s.price) > 0);

  const handleVisibilityChange = async (event) => {
    const newVisibility = event.target.checked;
    // Block going visible with no priced service; turning OFF is always allowed.
    if (newVisibility && !hasPricedService) {
      setVisibilitySuccess('');
      setVisibilityError(t('overview.visibilityNeedsService'));
      setTimeout(() => setVisibilityError(''), 6000);
      return;
    }
    setVisibilityLoading(true);
    setVisibilityError('');
    setVisibilitySuccess('');

    try {
      // The PUT is a full overwrite, so merge onto the LATEST server truth — never a
      // stale local copy — to avoid clobbering other fields.
      const latest = (await refreshProviderData()) || providerData;
      const updatedProviderData = {
        ...latest,
        visible: newVisibility
      };
      await providerApi.updateProvider(providerData.id, updatedProviderData);
      setVisible(newVisibility);
      // Keep shared provider data in sync so other screens don't revert this change
      await refreshProviderData();
      setVisibilitySuccess(newVisibility ? t('overview.visibilityNowVisible') : t('overview.visibilityNowHidden'));
      setTimeout(() => setVisibilitySuccess(''), 3000);
    } catch (err) {
      setVisibilityError(err.response?.data?.message || t('overview.visibilityUpdateFailed'));
      setTimeout(() => setVisibilityError(''), 5000);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const navItems = getNavItems(providerData.providerType).map((item) => (
    item.badgeKey === 'returns' ? { ...item, badge: openReturns } : item
  ));
  const currentNavItem = navItems.find((i) => i.value === currentTab);
  const pageTitle = currentTab === TAB_UPGRADE ? t('nav.upgrade') : (currentNavItem ? t(currentNavItem.labelKey) : t('nav.overview'));

  // Stat cards configuration per provider type
  const getStatCards = () => {
    const cards = [];
    if (providerData.providerType === 'STORE') {
      cards.push({ id: 'products', label: t('overview.statProducts'), value: stats?.products, caption: stats?.activeProducts != null ? t('overview.activeCount', { count: stats.activeProducts }) : null, Icon: Inventory2Icon, color: C.primary });
      cards.push({ id: 'orders', label: t('overview.statOrders'), value: stats?.orders, Icon: ReceiptLongIcon, color: C.blue });
    } else if (providerData.providerType === 'SERVICE') {
      cards.push({ id: 'services', label: t('overview.statServices'), value: stats?.services, Icon: DesignServicesIcon, color: C.primary });
      cards.push({ id: 'orders', label: t('overview.statOrders'), value: stats?.orders, Icon: ReceiptLongIcon, color: C.blue });
    } else if (providerData.providerType === 'INFLUENCER') {
      cards.push({ id: 'referrals', label: t('overview.statReferrals'), value: stats?.referrals, Icon: CampaignIcon, color: C.primary });
    }
    cards.push({
      id: 'visibility',
      label: t('overview.statVisibility'),
      value: visible ? t('overview.visibilityOn') : t('overview.visibilityOff'),
      Icon: VisibilityIcon,
      color: visible ? C.green : C.slate,
    });
    // Plan is not relevant for STORE providers (no Pro plan concept)
    if (providerData.providerType !== 'STORE') {
      cards.push({
        id: 'plan',
        label: t('overview.statPlan'),
        value: providerData.isProPlan ? t('plan.pro') : t('plan.free'),
        Icon: providerData.isProPlan ? WorkspacePremiumIcon : CheckCircleIcon,
        color: providerData.isProPlan ? C.amber : C.slate,
      });
    }
    return cards;
  };

  const statCards = getStatCards();

  const drawerContent = (
    <SidebarContent
      navItems={navItems}
      currentTab={currentTab}
      onSelect={handleSelectTab}
      currentUser={currentUser}
      providerType={providerData.providerType}
      onLogout={handleSignOut}
      isPro={plan.isPro}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop permanent sidebar */}
      {!isMobile && (
        <Box component="nav" sx={{ width: DRAWER_WIDTH, flexShrink: 0 }}>
          <Drawer
            variant="permanent"
            sx={{
              width: DRAWER_WIDTH,
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                border: 'none',
                overflowX: 'hidden',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      )}

      {/* Mobile temporary sidebar */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            zIndex: (t) => t.zIndex.drawer - 1,
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important', px: { xs: 2, sm: 3 } }}>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1.5, color: 'text.secondary', borderRadius: 1.5 }}
                size="small"
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.95rem', letterSpacing: '-0.01em' }}
            >
              {pageTitle}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
          <ConfirmationBanner />
          {typePaymentsEnabled ? (
            <PayoutOnboardingBanner
              providerData={providerData}
              onNavigateToPayments={() => handleSelectTab(TAB_PAYMENTS)}
            />
          ) : (!comingSoonDismissed && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={dismissComingSoon}>
              {t('comingSoon.paymentsBanner')}
            </Alert>
          ))}
          {currentTab === 0 && (
            <Box>
              {/* Provider header */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: alpha(C.primary, 0.12),
                        color: C.primary,
                      }}
                    >
                      <StorefrontIcon sx={{ fontSize: 34 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {providerData.title || t('overview.providerFallback')}
                        </Typography>
                        {plan.isVerified && (
                          <Box
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              bgcolor: alpha(C.green, 0.1), color: C.green,
                              border: '1px solid', borderColor: alpha(C.green, 0.35),
                              borderRadius: 999, px: 1.25, py: 0.25, fontSize: '0.72rem', fontWeight: 600,
                            }}
                          >
                            <VerifiedIcon sx={{ fontSize: 14 }} />
                            {t('plan.verified')}
                          </Box>
                        )}
                        {providerData.providerType !== 'STORE' && (
                          <Box
                            onClick={goToUpgrade}
                            sx={{
                              bgcolor: providerData.isProPlan ? alpha(C.amber, 0.12) : 'transparent',
                              color: providerData.isProPlan ? C.amber : 'text.secondary',
                              border: '1px solid', borderColor: 'divider',
                              borderRadius: 999, px: 1.25, py: 0.25, fontSize: '0.72rem', fontWeight: 600,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.75 },
                            }}
                          >
                            {providerData.isProPlan ? t('plan.pro') : t('plan.free')}
                          </Box>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {providerData.about || t('overview.noDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Become a Pro Banner — hidden for STORE providers */}
              {!providerData.isProPlan && providerData.providerType !== 'STORE' && (
                <Card
                  onClick={goToUpgrade}
                  data-testid="upgrade-banner"
                  sx={{
                    mb: 3,
                    background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    '&:hover': { opacity: 0.92 },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <WorkspacePremiumIcon sx={{ color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="white">
                          {t('overview.upgradeBannerTitle')}
                        </Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.85)">
                          {t('overview.upgradeBannerBody')}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography color="white" sx={{ fontSize: 24 }}>&rarr;</Typography>
                  </CardContent>
                </Card>
              )}

              {/* Alerts */}
              {visibilityError && <Alert severity="error" sx={{ mb: 2 }}>{visibilityError}</Alert>}
              {visibilitySuccess && <Alert severity="success" sx={{ mb: 2 }}>{visibilitySuccess}</Alert>}

              {/* Stat cards */}
              <Box
                sx={{
                  mb: 3,
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: `repeat(${statCards.length}, 1fr)`,
                  },
                }}
              >
                {statCards.map((card) => (
                  <StatCard
                    key={card.id}
                    label={card.label}
                    value={card.value}
                    caption={card.caption}
                    Icon={card.Icon}
                    color={card.color}
                    loading={statsLoading && ['products', 'orders', 'services', 'referrals'].includes(card.id)}
                  />
                ))}
              </Box>

              {/* Commission the store pays (STORE accounts only) */}
              {providerData.providerType === 'STORE' && <CommissionCard />}

              {/* Held payout total and next payout date (STORE accounts only) */}
              {providerData.providerType === 'STORE' && <PayoutsCard />}

              {/* Profile visibility */}
              <SectionCard title={t('overview.visibilityCardTitle')}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {visible ? t('overview.visibilityVisibleTitle') : t('overview.visibilityHiddenTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('overview.visibilityDescription')}
                    </Typography>
                    {!hasPricedService && (
                      <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                        {t('overview.visibilityNeedsService')}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={visible}
                        onChange={handleVisibilityChange}
                        // Can't switch ON without a priced service; still allow switching OFF a
                        // seeded profile that arrived visible-but-serviceless.
                        disabled={visibilityLoading || (!hasPricedService && !visible)}
                        color="primary"
                      />
                    }
                    label={visibilityLoading ? <CircularProgress size={20} /> : (visible ? t('overview.visibilityOn') : t('overview.visibilityOff'))}
                    labelPlacement="start"
                  />
                </Box>
              </SectionCard>
            </Box>
          )}

          {currentTab === 1 && (
            <ProviderProfile providerData={providerData} currencyCode={currencyCode} onUpgradeClick={goToUpgrade} />
          )}

          {currentTab === 2 && providerData.providerType === 'INFLUENCER' && (
            <InfluencerStats providerData={providerData} />
          )}

          {currentTab === 2 && providerData.providerType === 'SERVICE' && (
            <ServiceManagement providerData={providerData} />
          )}

          {currentTab === 2 && providerData.providerType === 'STORE' && (
            <StoreManagement providerData={providerData} />
          )}

          {currentTab === 3 && providerData.providerType === 'SERVICE' && (
            <ProGate providerData={providerData} feature={FEATURES.AVAILABILITY} onUpgradeClick={goToUpgrade}>
              <AvailabilitySettings providerData={providerData} />
            </ProGate>
          )}

          {currentTab === 3 && providerData.providerType === 'STORE' && (
            <OrdersManagement providerData={providerData} onUpgradeClick={goToUpgrade} onProviderRefresh={refreshProviderData} />
          )}

          {/* Free providers read, chat and take notes here; the calendar and proposals are gated
              INSIDE the page (owner decision 2026-10-18, calendar + notes design §3). */}
          {currentTab === 4 && providerData.providerType === 'SERVICE' && (
            <OrdersManagement providerData={providerData} onUpgradeClick={goToUpgrade} onProviderRefresh={refreshProviderData} />
          )}

          {currentTab === 5 && providerData.providerType === 'SERVICE' && (
            typePaymentsEnabled ? (
              <ProGate providerData={providerData} feature={FEATURES.PAYMENTS} onUpgradeClick={goToUpgrade}>
                <PaymentSetup providerData={providerData} />
              </ProGate>
            ) : (
              // Payments are off platform-wide: the coming-soon teaser, for Pro and Free alike.
              // A Pro provider has nothing to upgrade to, so no CTA for them.
              <ProTeaser
                feature={FEATURES.PAYMENTS}
                onUpgradeClick={providerData.isProPlan ? undefined : goToUpgrade}
              />
            )
          )}

          {currentTab === 6 && providerData.providerType === 'SERVICE' && (
            <MyAudience providerData={providerData} onNavigateToOrders={() => handleSelectTab(4)} />
          )}

          {currentTab === TAB_RETURNS && providerData.providerType === 'STORE' && (
            <StoreReturns onChanged={refreshOpenReturns} />
          )}

          {currentTab === 4 && providerData.providerType === 'STORE' && (
            typePaymentsEnabled ? (
              <PaymentSetup providerData={providerData} />
            ) : (
              <Alert severity="info">{t('comingSoon.paymentsBanner')}</Alert>
            )
          )}

          {currentTab === 3 && providerData.providerType === 'INFLUENCER' && (
            <MyAudience providerData={providerData} onNavigateToOrders={null} />
          )}

          {currentTab === 5 && providerData.providerType === 'STORE' && (
            <MyAudience providerData={providerData} onNavigateToOrders={() => handleSelectTab(3)} />
          )}

          {currentTab === 7 && providerData.providerType === 'SERVICE' && (
            <ProGate providerData={providerData} feature={FEATURES.ANALYTICS} onUpgradeClick={goToUpgrade}>
              <AnalyticsPage providerData={providerData} onNavigateToOrders={() => handleSelectTab(4)} />
            </ProGate>
          )}

          {currentTab === 8 && providerData.providerType === 'SERVICE' && (
            <ProGate providerData={providerData} feature={FEATURES.REVIEWS} onUpgradeClick={goToUpgrade}>
              <ReviewsPage providerData={providerData} />
            </ProGate>
          )}

          {currentTab === TAB_UPGRADE && providerData.providerType === 'SERVICE' && (
            <UpgradePage providerData={providerData} />
          )}

          {currentTab === TAB_PROMOTIONS && providerData.providerType === 'SERVICE' && (
            <PromotionsPage />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
